import { getReservationTypeMappings } from "@/api/reservationTypeMappings/thunks";
import { ReservationTypeMapping } from "@/api/reservationTypeMappings/types";
import { createSlice } from "@reduxjs/toolkit";

interface ReservationTypeMappingsState {
  mappings: ReservationTypeMapping[];
  mappingsMap: Record<string, ReservationTypeMapping>;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: ReservationTypeMappingsState = {
  mappings: [],
  mappingsMap: {},
  loading: false,
  error: null,
  initialized: false,
};

// Default fallback for unknown types
const DEFAULT_MAPPING = {
  displayCategory: "OTHER_BLOCK" as const,
  displayLabel: "Other Block",
  colorClass: "bg-chicago-100 text-chicago-950",
};

const reservationTypeMappingsSlice = createSlice({
  name: "ReservationTypeMappingsSlice",
  initialState,
  reducers: {
    clearMappings: (state) => {
      state.mappings = [];
      state.mappingsMap = {};
      state.loading = false;
      state.error = null;
      state.initialized = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReservationTypeMappings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReservationTypeMappings.fulfilled, (state, action) => {
        state.mappings = action.payload;
        state.mappingsMap = Object.fromEntries(
          action.payload.map((m) => [m.streamlineType, m])
        );
        state.loading = false;
        state.error = null;
        state.initialized = true;
      })
      .addCase(getReservationTypeMappings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to get reservation type mappings";
      });
  },
});

// Helper function to get mapping for a type
export const getMappingForType = (
  mappingsMap: Record<string, ReservationTypeMapping>,
  type: string
): { displayCategory: string; displayLabel: string; colorClass: string } => {
  const mapping = mappingsMap[type];
  if (mapping) {
    return {
      displayCategory: mapping.displayCategory,
      displayLabel: mapping.displayLabel,
      colorClass: mapping.colorClass,
    };
  }
  return DEFAULT_MAPPING;
};

export const { clearMappings } = reservationTypeMappingsSlice.actions;
export default reservationTypeMappingsSlice.reducer;
